<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import CategoryItemBadge from './CategoryItemBadge.vue';

const props = defineProps({
  images: { type: Array, default: () => [] },
  alt: { type: String, default: '' },
  badgeText: { type: String, default: '' },
  badgeVariant: { type: String, default: 'custom' }
});

const activeIndex = ref(0);
const slideCount = computed(() => Math.max(1, props.images.length));
const canNavigate = computed(() => props.images.length > 1);

function goTo(index) {
  if (!props.images.length) return;
  const max = props.images.length - 1;
  activeIndex.value = Math.min(max, Math.max(0, index));
}

function step(delta) {
  goTo(activeIndex.value + delta);
}

watch(
  () => props.images.length,
  () => {
    activeIndex.value = 0;
  }
);

let timer;
onMounted(() => {
  if (!canNavigate.value) return;
  timer = window.setInterval(() => {
    goTo(activeIndex.value + 1 >= props.images.length ? 0 : activeIndex.value + 1);
  }, 6000);
});

onUnmounted(() => {
  if (timer) window.clearInterval(timer);
});
</script>

<template>
  <div class="vsb-cart-gallery">
    <CategoryItemBadge
      v-if="badgeText"
      :text="badgeText"
      :variant="badgeVariant"
    />

    <div class="vsb-cart-gallery__viewport">
      <img
        v-if="images[activeIndex]"
        :src="images[activeIndex]"
        :alt="alt"
        class="vsb-cart-gallery__img"
        loading="lazy"
        decoding="async"
      />
    </div>

    <template v-if="canNavigate">
      <button type="button" class="vsb-cart-gallery__nav vsb-cart-gallery__nav--prev" @click="step(-1)">
        ‹
      </button>
      <button type="button" class="vsb-cart-gallery__nav vsb-cart-gallery__nav--next" @click="step(1)">
        ›
      </button>
      <div class="vsb-cart-gallery__dots" role="tablist" :aria-label="alt">
        <button
          v-for="(_, i) in images"
          :key="i"
          type="button"
          class="vsb-cart-gallery__dot"
          :class="{ 'vsb-cart-gallery__dot--active': i === activeIndex }"
          :aria-selected="i === activeIndex"
          @click="goTo(i)"
        />
      </div>
    </template>
  </div>
</template>

<style scoped>
.vsb-cart-gallery {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  background: #f1f5f9;
  aspect-ratio: var(--vsb-cart-gallery-aspect, 4 / 3);
}

.vsb-cart-gallery__viewport {
  width: 100%;
  height: 100%;
}

.vsb-cart-gallery__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.vsb-cart-gallery__nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: var(--vsb-cart-gallery-btn-bg, rgba(255, 255, 255, 0.92));
  color: #0f172a;
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.vsb-cart-gallery__nav--prev {
  left: 10px;
}
.vsb-cart-gallery__nav--next {
  right: 10px;
}

.vsb-cart-gallery__dots {
  position: absolute;
  bottom: 12px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 6px;
  z-index: 2;
}

.vsb-cart-gallery__dot {
  width: 8px;
  height: 8px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: var(--vsb-cart-gallery-dot, #cbd5e1);
  cursor: pointer;
}

.vsb-cart-gallery__dot--active {
  background: var(--vsb-cart-gallery-dot-active, #0b3d91);
  transform: scale(1.15);
}
</style>
